import React, { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Loader2, ShieldOff, CheckCircle2 } from "lucide-react";
import { unsubscribeFromArchive } from "../lib/api";

const Unsubscribe = () => {
  const [params] = useSearchParams();
  const token = params.get("token");
  const [state, setState] = useState("loading"); // loading | ok | error
  const [email, setEmail] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!token) {
      setError("This link is missing its access token.");
      setState("error");
      return;
    }
    let mounted = true;
    (async () => {
      try {
        const res = await unsubscribeFromArchive(token);
        if (!mounted) return;
        // Both "unsubscribed" and "already_unsubscribed" are user-success.
        // We don't differentiate — confirms the contact is off the list.
        setEmail(res.email || null);
        setState("ok");
      } catch (e) {
        if (!mounted) return;
        setError(e?.response?.data?.detail || "We could not process this unsubscribe link.");
        setState("error");
      }
    })();
    return () => { mounted = false; };
  }, [token]);

  return (
    <div className="max-w-2xl mx-auto px-6 md:px-10 py-20" data-testid="unsubscribe-page">
      <div className="border border-[#2D2A26] bg-[#0F0F0F] aged-paper p-8 md:p-10 relative overflow-hidden">
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#D92D20]/60 to-transparent" />
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#D92D20] mb-3">
          // SUBSCRIBER LEDGER · REMOVAL REQUEST
        </div>

        {state === "loading" && (
          <div className="flex items-center gap-3 text-[#A39E93] font-mono text-xs uppercase tracking-widest">
            <Loader2 className="animate-spin" size={14} /> PROCESSING…
          </div>
        )}

        {state === "ok" && (
          <div data-testid="unsubscribe-ok">
            <div className="flex items-center gap-2 mb-3 text-[#5DD4B3]">
              <CheckCircle2 size={16} />
              <span className="font-mono text-[10px] uppercase tracking-[0.3em]">
                CONTACT REMOVED
              </span>
            </div>
            <h1 className="font-display text-3xl md:text-4xl text-[#E4DFD3] mb-3">
              You&apos;re off the list
            </h1>
            <p className="text-[#A39E93] leading-relaxed">
              {email ? (
                <>
                  <code className="text-[#E4DFD3]">{email}</code> has been removed from the
                  Archive&apos;s briefing ledger.{" "}
                </>
              ) : (
                "Your address has been removed from the briefing ledger. "
              )}
              No further dispatches will be sent.
            </p>
          </div>
        )}

        {state === "error" && (
          <div data-testid="unsubscribe-error">
            <div className="flex items-center gap-2 mb-3 text-[#D92D20]">
              <ShieldOff size={16} />
              <span className="font-mono text-[10px] uppercase tracking-[0.3em]">
                LINK INVALID
              </span>
            </div>
            <h1 className="font-display text-3xl md:text-4xl text-[#E4DFD3] mb-3">
              We couldn&apos;t process this link
            </h1>
            <p className="text-[#A39E93] leading-relaxed">{error}</p>
          </div>
        )}

        <div className="mt-8 flex flex-wrap gap-3">
          <Link
            to="/"
            data-testid="unsubscribe-home-link"
            className="inline-flex items-center gap-2 border border-[#454038] hover:border-[#D92D20] text-[#A39E93] hover:text-[#E4DFD3] px-4 py-2.5 font-mono text-[10px] uppercase tracking-[0.25em]"
          >
            Back to The Archive
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Unsubscribe;
